import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Zap, TrendingUp, Ticket, DollarSign, Bell, AlertTriangle, CheckCircle2, Clock, PowerOff, Pencil } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import LotteryFeedPanel from './LotteryFeedPanel';
import StuckTicketsPanel from './StuckTicketsPanel';
import CorrectDrawDialog from './CorrectDrawDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const LotteryAdminTab = ({
    drawState, setDrawState,
    drawPickType, setDrawPickType,
    drawNumbers, setDrawNumbers,
    drawDate, setDrawDate,
    drawTime, setDrawTime,
    drawLoading,
    handleCreateDraw,
    lotteryDraws,
    lotteryTickets,
    lotteryAdminSettings,
    setLotteryAdminSettings,
    editRules,
    setEditRules,
    fetchLotteryData,
}) => {
    const [voidingId, setVoidingId] = useState(null);
    const [showAllDraws, setShowAllDraws] = useState(false);
    const [showAllTickets, setShowAllTickets] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    // Admin void — prompts for refund choice EACH time (owner request).
    const voidTicket = async (t) => {
        if (!t || voidingId) return;
        const label = Array.isArray(t.numbers) ? t.numbers.join('-') : (t.numbers ?? 'ticket');
        if (!window.confirm(`Void ${t.user_name || 'player'}'s ${t.state} ${label} ticket?`)) return;
        const refund = window.confirm('Refund the wager back to the player?\n\nOK = refund   •   Cancel = void with NO refund');
        setVoidingId(t.id);
        try {
            const { data } = await axios.post(`${API}/admin/lottery/ticket/${t.id}/void`,
                { refund }, { withCredentials: true });
            toast.success(`Voided ${data.voided} ticket(s)${refund ? ` — $${Number(data.refunded || 0).toFixed(2)} refunded` : ' (no refund)'}`);
            fetchLotteryData && fetchLotteryData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Void failed');
        } finally {
            setVoidingId(null);
        }
    };
    const handleSaveSettings = async () => {
        try {
            await axios.put(`${API}/lottery/admin/settings`, {
                cutoffs: lotteryAdminSettings.cutoffs,
                payouts_pick3: lotteryAdminSettings.payouts_pick3,
                payouts_pick4: lotteryAdminSettings.payouts_pick4,
                rules: editRules,
            }, { withCredentials: true });
            toast.success('Lottery settings saved');
        } catch (e) {
            toast.error('Failed to save settings');
        }
    };

    const updatePayout = (pick, key, val) => {
        const k = `payouts_pick${pick}`;
        const cur = lotteryAdminSettings[k] || {};
        setLotteryAdminSettings({
            ...lotteryAdminSettings,
            [k]: { ...cur, [key]: Number(val) || 0 },
        });
    };

    const [correctDialogOpen, setCorrectDialogOpen] = useState(false);
    const [correctTarget, setCorrectTarget] = useState(null);
    const openCorrect = (draw) => { setCorrectTarget(draw); setCorrectDialogOpen(true); };

    const [autobuyStats, setAutobuyStats] = useState(null);
    const [autobuyToday, setAutobuyToday] = useState(null);
    const fetchAutobuyStats = async () => {
        try {
            const [s, t] = await Promise.all([
                axios.get(`${API}/lottery/admin/autobuy-stats`, { withCredentials: true }),
                axios.get(`${API}/lottery/admin/autobuy-today`, { withCredentials: true }),
            ]);
            setAutobuyStats(s.data);
            setAutobuyToday(t.data);
        } catch { /* ignore */ }
    };
    useEffect(() => { fetchAutobuyStats(); }, []);

    const handleAdminDisableAutobuy = async (userId, userEmail) => {
        if (!window.confirm(`Turn off auto-buy for ${userEmail}? They will be notified.`)) return;
        try {
            await axios.post(`${API}/lottery/admin/autobuy/${userId}/disable`, {}, { withCredentials: true });
            toast.success('Auto-buy disabled');
            fetchAutobuyStats();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to disable');
        }
    };

    return (
        <div className="space-y-6">
            {/* Auto-fetch RapidAPI feed panel */}
            <LotteryFeedPanel />
            <StuckTicketsPanel />

            {/* Auto-buy adoption stats */}
            <div className="card-dark p-4 border-[#39FF14]/20" data-testid="autobuy-stats-card">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                        <Zap className="w-4 h-4 text-[#39FF14]" />
                        Auto-buy Adoption
                    </h3>
                    <Button size="sm" variant="outline" onClick={fetchAutobuyStats}
                        className="border-[#39FF14]/30 text-[#39FF14] h-7 text-[11px]"
                        data-testid="autobuy-stats-refresh">
                        <RefreshCw className="w-3 h-3 mr-1" />Refresh
                    </Button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Stat icon={Zap}        color="#39FF14" label="Players Enabled"   value={autobuyStats?.enabled_count ?? '—'} />
                    <Stat icon={DollarSign} color="#FFD700" label="Daily Commitment"  value={`$${(autobuyStats?.daily_commitment || 0).toFixed(2)}`} />
                    <Stat icon={TrendingUp} color="#3B82F6" label="Monthly Projection" value={`$${(autobuyStats?.monthly_projection || 0).toFixed(0)}`} />
                    <Stat icon={Ticket}     color="#FF007F" label="Tickets Fired (7d)" value={autobuyStats?.tickets_fired_7d ?? '—'} />
                </div>
                <p className="text-[10px] text-[#A0AAB2] mt-2">
                    Daily commitment = sum of (states × bet types × wager) across enabled auto-buys. Leading indicator of recurring lottery revenue.
                </p>
            </div>

            {/* Auto-buy fired today */}
            <div className="card-dark p-4" data-testid="autobuy-today-card">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                        <Bell className="w-4 h-4 text-[#FFD700]" />
                        Auto-buy Fired Today
                        {autobuyToday?.summary?.total_runs ? (
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-[#FFD700]/20 text-[#FFD700]">{autobuyToday.summary.total_runs}</span>
                        ) : null}
                    </h3>
                    <span className="text-[10px] text-[#A0AAB2] font-mono" data-testid="autobuy-today-date">{autobuyToday?.date || ''}</span>
                </div>
                {autobuyToday && autobuyToday.summary && autobuyToday.summary.total_runs > 0 ? (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                            <Stat icon={CheckCircle2}   color="#39FF14" label="Successful"      value={autobuyToday.summary.ok} />
                            <Stat icon={AlertTriangle}  color="#FF3B30" label="Insufficient $"  value={autobuyToday.summary.insufficient} />
                            <Stat icon={Clock}          color="#FF8C00" label="Skipped (late)"  value={autobuyToday.summary.skipped_late} />
                            <Stat icon={Ticket}         color="#FF007F" label="Tickets / $"     value={`${autobuyToday.summary.tickets_total} / $${autobuyToday.summary.cost_total.toFixed(2)}`} />
                        </div>
                        <div className="overflow-x-auto rounded-lg border border-white/5">
                            <table className="w-full text-xs">
                                <thead className="bg-[#0a0a0a] text-[#A0AAB2]">
                                    <tr>
                                        <th className="text-left p-2">Player</th>
                                        <th className="text-left p-2">Setup</th>
                                        <th className="text-right p-2">Tickets</th>
                                        <th className="text-right p-2">Cost</th>
                                        <th className="text-left p-2">Status</th>
                                        <th className="text-right p-2">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {autobuyToday.runs.map((r, i) => (
                                        <tr key={i} className="border-t border-white/5 hover:bg-white/5" data-testid={`autobuy-today-row-${i}`}>
                                            <td className="p-2">
                                                <p className="text-white font-medium">{r.user_name || '—'}</p>
                                                <p className="text-[9px] text-[#A0AAB2]">{r.user_email}</p>
                                            </td>
                                            <td className="p-2 text-[#A0AAB2]">
                                                <span className="font-mono">{(r.states || []).join('+')}</span>{' · '}
                                                <span className="font-mono text-white">{(r.numbers || []).join('-')}</span>{' · '}
                                                <span className="text-[10px]">{r.bet_type === 'straight_box' ? 'Str+Box' : r.bet_type}</span>{' · '}
                                                <span className="font-mono">${r.amount}</span>
                                            </td>
                                            <td className="p-2 text-right font-mono text-white">{r.tickets}</td>
                                            <td className="p-2 text-right font-mono text-[#FFD700]">${(r.cost || 0).toFixed(2)}</td>
                                            <td className="p-2">
                                                {r.status === 'ok' && <span className="px-1.5 py-0.5 rounded text-[9px] bg-[#39FF14]/20 text-[#39FF14]">OK</span>}
                                                {r.status === 'insufficient_credits' && <span className="px-1.5 py-0.5 rounded text-[9px] bg-[#FF3B30]/20 text-[#FF3B30]">NO FUNDS</span>}
                                                {r.status === 'skipped_late' && <span className="px-1.5 py-0.5 rounded text-[9px] bg-[#FF8C00]/20 text-[#FF8C00]">LATE</span>}
                                            </td>
                                            <td className="p-2 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() => handleAdminDisableAutobuy(r.user_id, r.user_email)}
                                                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                                                    data-testid={`autobuy-today-disable-${i}`}
                                                    title="Turn off auto-buy for this player"
                                                >
                                                    <PowerOff className="w-3 h-3" />
                                                    Disable
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : (
                    <p className="text-center py-4 text-[#A0AAB2] text-xs" data-testid="autobuy-today-empty">
                        No auto-buys have run yet today.
                    </p>
                )}
            </div>

            {/* Enter Draw */}
            <div className="card-dark p-6">
                <h3 className="text-lg font-bold text-white mb-4">Enter Winning Numbers</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <select value={drawState} onChange={e => setDrawState(e.target.value)}
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg text-white px-3 py-2 text-sm">
                        {['IL', 'MS', 'TN'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={drawPickType} onChange={e => { const pt = parseInt(e.target.value); setDrawPickType(pt); setDrawNumbers(Array(pt).fill(null)); }}
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg text-white px-3 py-2 text-sm">
                        <option value={3}>Pick 3</option>
                        <option value={4}>Pick 4</option>
                    </select>
                    <input type="date" value={drawDate} onChange={e => setDrawDate(e.target.value)}
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg text-white px-3 py-2 text-sm" />
                    <select value={drawTime} onChange={e => setDrawTime(e.target.value)}
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg text-white px-3 py-2 text-sm">
                        <option value="midday">Midday</option>
                        <option value="evening">Evening</option>
                    </select>
                </div>
                <div className="flex gap-2 justify-center mb-4">
                    {drawNumbers.map((n, i) => (
                        <select key={i} value={n === null ? '' : n} onChange={e => {
                            const nums = [...drawNumbers]; nums[i] = parseInt(e.target.value); setDrawNumbers(nums);
                        }} className="w-14 h-14 rounded-xl bg-[#0a0a0a] border-2 border-[#FFD700]/30 text-[#FFD700] text-xl font-black text-center appearance-none">
                            <option value="" disabled>-</option>
                            {[0,1,2,3,4,5,6,7,8,9].map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                    ))}
                </div>
                <Button onClick={handleCreateDraw} disabled={drawLoading || drawNumbers.some(n => n === null)}
                    className="w-full bg-[#FFD700] text-black font-bold" data-testid="submit-draw-btn">
                    {drawLoading ? 'Processing...' : 'Submit Draw & Settle Tickets'}
                </Button>
            </div>

            {/* Recent Draws */}
            <div className="card-dark p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white">Recent Draws</h3>
                    <Button size="sm" variant="outline" onClick={fetchLotteryData} className="border-[#FFD700]/30 text-[#FFD700]">
                        <RefreshCw className="w-3 h-3 mr-1" />Refresh
                    </Button>
                </div>
                {lotteryDraws.length === 0 ? (
                    <p className="text-[#A0AAB2] text-center py-4">No draws yet. Click Refresh to load.</p>
                ) : (
                    <div className="space-y-2">
                        {(showAllDraws ? lotteryDraws : lotteryDraws.slice(0, 10)).map(d => (
                            <div key={d.id} className="flex items-center justify-between p-3 bg-[#0a0a0a] rounded-lg" data-testid={`draw-row-${d.id}`}>
                                <div>
                                    <span className="text-xs font-bold text-[#A0AAB2]">{d.state} Pick {d.pick_type}</span>
                                    <span className="text-[10px] text-[#666] ml-2">{d.draw_date} {d.drawing_time}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="flex gap-1">
                                        {d.winning_numbers.map((n, i) => (
                                            <span key={i} className="w-7 h-7 rounded bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] text-xs font-bold flex items-center justify-center">{n}</span>
                                        ))}
                                    </div>
                                    <Button size="sm" variant="outline"
                                        onClick={() => openCorrect(d)}
                                        title="Correct numbers or date"
                                        className="h-7 px-2 border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                                        data-testid={`correct-draw-btn-${d.id}`}>
                                        <Pencil className="w-3 h-3" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {lotteryDraws.length > 10 && (
                    <button type="button" onClick={() => setShowAllDraws(v => !v)}
                        className="mt-3 w-full text-xs text-[#FFD700]/90 hover:text-[#FFD700] underline underline-offset-2"
                        data-testid="lottery-draws-see-all">
                        {showAllDraws ? 'Show less' : `See all ${lotteryDraws.length} draws`}
                    </button>
                )}
            </div>

            {/* All Lottery Tickets */}
            <div className="card-dark p-6">
                <h3 className="text-lg font-bold text-white mb-4">All Tickets ({lotteryTickets.length})</h3>
                {lotteryTickets.length === 0 ? (
                    <p className="text-[#A0AAB2] text-center py-4">No tickets yet</p>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-white/10">
                                    <TableHead className="text-[#A0AAB2]">Customer</TableHead>
                                    <TableHead className="text-[#A0AAB2]">Numbers</TableHead>
                                    <TableHead className="text-[#A0AAB2]">Type</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Wager</TableHead>
                                    <TableHead className="text-[#A0AAB2]">Status</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Payout</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(showAllTickets ? lotteryTickets : lotteryTickets.slice(0, 10)).map((t, i) => {
                                    const isCombo = t.is_combo;
                                    const numbersArr = Array.isArray(t.numbers) ? t.numbers : null;
                                    const bulkLabel = !numbersArr
                                        ? (t.bet_type === 'trips' ? 'ALL TRIPLES' : t.bet_type === 'combo_all' ? 'ALL COMBOS' : String(t.numbers))
                                        : null;
                                    const typeLabel =
                                        t.bet_type === 'straight_box' ? 'Str+Box' :
                                        t.bet_type === 'combo' ? `Combo (${t.ways || ''}-way)` :
                                        t.bet_type === 'trips' ? 'All Triples' :
                                        t.bet_type === 'combo_all' ? 'All Combos' :
                                        t.bet_type;
                                    const isEvening = (t.drawing_time || '').toLowerCase() === 'evening';
                                    return (
                                    <TableRow key={t.id || i} className="border-white/5">
                                        <TableCell>
                                            <p className="text-white text-sm">{t.user_name}</p>
                                            <p className="text-[10px] text-[#A0AAB2]">{t.user_email}</p>
                                        </TableCell>
                                        <TableCell>
                                            {numbersArr ? (
                                                <div className="flex gap-1">
                                                    {numbersArr.map((n, j) => (
                                                        <span key={j} className="w-6 h-6 rounded bg-[#FFD700]/10 text-[#FFD700] text-xs font-bold flex items-center justify-center">{n}</span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="inline-block px-2 py-0.5 rounded bg-[#FF6B35]/15 text-[#FF6B35] text-[10px] font-black uppercase tracking-widest">
                                                    {bulkLabel}
                                                </span>
                                            )}
                                            <div className="flex items-center gap-1 mt-1">
                                                <span className="text-[10px] text-[#666]">{t.state} P{t.pick_type} {t.draw_date}</span>
                                                <span
                                                    className={`text-[9px] px-1.5 py-0.5 rounded font-black uppercase tracking-wider ${
                                                        isEvening
                                                            ? 'bg-[#1E3A8A]/40 text-[#93C5FD] border border-[#3B82F6]/40'
                                                            : 'bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30'
                                                    }`}
                                                    data-testid={`admin-ticket-draw-time-${t.id}`}
                                                    title={isEvening ? 'Evening draw' : 'Midday draw'}
                                                >
                                                    {isEvening ? 'Evening' : 'Midday'}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-xs text-[#A0AAB2]">
                                            {typeLabel}
                                            {isCombo && t.ways > 1 && (
                                                <span className="block text-[9px] text-[#666]">{t.ways} tickets</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-white">
                                            {isCombo && t.total_cost
                                                ? <>
                                                    {Number(t.total_cost).toFixed(2)}
                                                    <span className="block text-[9px] text-[#666]">${Number(t.amount_per_way || t.amount).toFixed(2)}/way</span>
                                                  </>
                                                : Number(t.amount).toFixed(2)}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                t.status === 'won' ? 'bg-[#39FF14]/20 text-[#39FF14]' :
                                                t.status === 'lost' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' :
                                                t.status === 'void' ? 'bg-[#666]/30 text-[#999]' :
                                                'bg-[#FFD700]/20 text-[#FFD700]'
                                            }`}>{t.status.toUpperCase()}</span>
                                            {t.status === 'pending' && (
                                                <button
                                                    type="button"
                                                    onClick={() => voidTicket(t)}
                                                    disabled={voidingId === t.id}
                                                    className="mt-1 block text-[10px] px-2 py-0.5 rounded-md bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF6B6B] hover:bg-[#FF3B30]/25 transition-colors disabled:opacity-50"
                                                    data-testid={`admin-void-ticket-${t.id}`}
                                                    title="Void this pending ticket"
                                                >
                                                    {voidingId === t.id ? 'Voiding…' : 'Void'}
                                                </button>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-[#39FF14]">{t.payout > 0 ? `+${Number(t.payout).toFixed(2)}` : '-'}</TableCell>
                                    </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                )}
                {lotteryTickets.length > 10 && (
                    <button type="button" onClick={() => setShowAllTickets(v => !v)}
                        className="mt-3 w-full text-xs text-[#FFD700]/90 hover:text-[#FFD700] underline underline-offset-2"
                        data-testid="lottery-tickets-see-all">
                        {showAllTickets ? 'Show less' : `See all ${lotteryTickets.length} tickets`}
                    </button>
                )}
            </div>

            {/* Lottery Settings — tucked behind its own button. */}
            <div className="card-dark p-6">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-white">Lottery Settings</h3>
                    <Button size="sm" variant="outline" onClick={() => setShowSettings(v => !v)}
                        className="border-[#FFD700]/30 text-[#FFD700]" data-testid="lottery-settings-toggle">
                        {showSettings ? 'Hide' : 'Open Settings'}
                    </Button>
                </div>
                {showSettings && (
                <div className="mt-4">
                {lotteryAdminSettings ? (
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm font-bold text-[#FFD700] mb-2">Cutoff Times (Central Time)</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {['IL_midday', 'IL_evening', 'MS_midday', 'MS_evening', 'TN_midday', 'TN_evening'].map(key => (
                                    <div key={key} className="flex items-center gap-2">
                                        <Label className="text-[#A0AAB2] text-xs w-24 shrink-0">{key.replace('_', ' ')}</Label>
                                        <Input value={lotteryAdminSettings.cutoffs?.[key] || ''} onChange={e => {
                                            setLotteryAdminSettings(prev => ({
                                                ...prev, cutoffs: { ...prev.cutoffs, [key]: e.target.value }
                                            }));
                                        }} className="bg-[#0a0a0a] border-white/10 text-white text-xs h-8" />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="text-sm font-bold text-[#FFD700] mb-2">Payout Multipliers</p>
                            <p className="text-[10px] text-[#A0AAB2] mb-3">
                                Win = wager × multiplier. Combo (a.k.a. &ldquo;ways&rdquo;) is paid PER matching permutation,
                                so you typically set it equal to or slightly below the Straight rate.
                            </p>
                            {[3, 4].map(pick => {
                                const k = `payouts_pick${pick}`;
                                const p = lotteryAdminSettings[k] || {};
                                return (
                                    <div key={pick} className="mb-3">
                                        <p className="text-xs font-bold text-white mb-1">Pick {pick}</p>
                                        <div className="grid grid-cols-3 gap-2">
                                            {['straight', 'box', 'combo'].map(kind => (
                                                <div key={kind} className="flex items-center gap-1">
                                                    <Label className="text-[#A0AAB2] text-[10px] w-16 shrink-0 capitalize">{kind}</Label>
                                                    <Input
                                                        type="number"
                                                        value={p[kind] ?? ''}
                                                        onChange={e => updatePayout(pick, kind, e.target.value)}
                                                        className="bg-[#0a0a0a] border-white/10 text-white text-xs h-8"
                                                        data-testid={`payout-pick${pick}-${kind}`}
                                                    />
                                                    <span className="text-[#A0AAB2] text-[10px]">×</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div>
                            <p className="text-sm font-bold text-[#FFD700] mb-2">Rules & Regulations</p>
                            <textarea value={editRules} onChange={e => setEditRules(e.target.value)}
                                rows={8} className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg text-white text-xs p-3 font-mono" />
                        </div>
                        <Button onClick={handleSaveSettings} className="bg-[#FFD700] text-black font-bold" data-testid="save-lottery-settings-btn">
                            Save Settings
                        </Button>
                    </div>
                ) : (
                    <p className="text-[#A0AAB2] text-center py-4">Click Refresh to load settings</p>
                )}
                </div>
                )}
            </div>

            <CorrectDrawDialog
                open={correctDialogOpen}
                onOpenChange={setCorrectDialogOpen}
                draw={correctTarget}
                onCorrected={fetchLotteryData}
            />
        </div>
    );
};

const Stat = ({ icon: Icon, color, label, value }) => (
    <div className="bg-[#0a0a0a] rounded-lg p-2.5 border border-white/5">
        <div className="flex items-center gap-1.5 mb-1">
            {Icon && <Icon className="w-3 h-3" style={{ color }} />}
            <p className="text-[9px] uppercase font-bold text-[#A0AAB2] tracking-wide truncate">{label}</p>
        </div>
        <p className="font-mono font-bold text-sm" style={{ color }}>{value}</p>
    </div>
);
